import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import CustomerProfilePanel, {
  PROFILE_SECTION_DEFINITIONS,
  type BackendProfileSections,
  type ScrapedPage,
} from '../components/customer/CustomerProfilePanel';
import { type CtaEntry } from '../components/customer/CtaTable';
import { useAdminApi } from '../hooks/useAdminApi';
import { sanitizeWorkspaceSlug } from '../utils/workspace';

interface SetupProfileReviewPageProps {
  isDarkMode: boolean;
}

interface WorkspaceProfile {
  workspaceSlug: string;
  website: string;
  productWebsite: string;
  lastScrapedAt: string | null;
  profile_sections: Partial<BackendProfileSections>;
}

const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocalhost ? 'http://localhost:5000' : '';

const emptySections = (): BackendProfileSections => (
  Object.fromEntries(PROFILE_SECTION_DEFINITIONS.map((section) => [section.key, ''])) as BackendProfileSections
);

const normalizeSections = (value: unknown): BackendProfileSections => {
  const source = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  const normalized = emptySections();
  PROFILE_SECTION_DEFINITIONS.forEach((section) => {
    const raw = source[section.key];
    normalized[section.key] = typeof raw === 'string' ? raw : '';
  });
  return normalized;
};

const normalizeScrapedPages = (payload: unknown): ScrapedPage[] => {
  if (!payload || typeof payload !== 'object') {
    return [];
  }
  const pages = Array.isArray((payload as Record<string, unknown>).scrapedPages)
    ? (payload as Record<string, unknown>).scrapedPages as unknown[]
    : [];
  return pages.map((entry) => {
    const row = entry && typeof entry === 'object' ? entry as Record<string, unknown> : {};
    return {
      url: typeof row.url === 'string' ? row.url : '',
      sourceType: typeof row.sourceType === 'string' ? row.sourceType : 'website',
      title: typeof row.title === 'string' ? row.title : '',
      metaDescription: typeof row.metaDescription === 'string' ? row.metaDescription : '',
      extractedText: typeof row.extractedText === 'string' ? row.extractedText : '',
      scrapedAt: typeof row.scrapedAt === 'string' ? row.scrapedAt : undefined,
      faviconUrl: typeof row.faviconUrl === 'string' ? row.faviconUrl : undefined,
      logoUrl: typeof row.logoUrl === 'string' ? row.logoUrl : undefined,
      titleFontFamily: typeof row.titleFontFamily === 'string' ? row.titleFontFamily : undefined,
      titleColor: typeof row.titleColor === 'string' ? row.titleColor : undefined,
    };
  });
};

const adminAuthHeaders = (): Record<string, string> => {
  const adminToken = window.sessionStorage.getItem('admin_token');
  return {
    'Content-Type': 'application/json',
    ...(adminToken ? { Authorization: `Bearer ${adminToken}` } : {}),
  };
};

const SetupProfileReviewPage: React.FC<SetupProfileReviewPageProps> = ({ isDarkMode }) => {
  const navigate = useNavigate();
  const {
    customerSlug: customerSlugParam,
    workspaceSlug: workspaceSlugParam,
    agentSlug: agentSlugParam,
  } = useParams<{ customerSlug: string; workspaceSlug: string; agentSlug: string }>();
  const { isAuthenticated } = useAdminApi();
  const customerSlug = sanitizeWorkspaceSlug(customerSlugParam);
  const workspaceSlug = sanitizeWorkspaceSlug(workspaceSlugParam || 'main');
  const agentSlug = sanitizeWorkspaceSlug(agentSlugParam || 'sally');
  const [profile, setProfile] = useState<WorkspaceProfile | null>(null);
  const [scrapedPages, setScrapedPages] = useState<ScrapedPage[]>([]);
  const [ctas, setCtas] = useState<CtaEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [profileMissing, setProfileMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      setProfileMissing(false);
      if (!isAuthenticated) {
        setError('Login erforderlich.');
        setIsLoading(false);
        return;
      }
      try {
        const encoded = encodeURIComponent(customerSlug);
        const headers = adminAuthHeaders();
        const [profileResponse, scrapedResponse, ctaResponse] = await Promise.all([
          fetch(`${API_BASE}/api/customer/workspace/${encoded}`, { headers }),
          fetch(`${API_BASE}/api/customer/workspace/${encoded}/scraped-content`, { headers }),
          fetch(`${API_BASE}/api/customer/workspace/${encoded}/ctas`, { headers }),
        ]);
        const [profilePayload, scrapedPayload, ctaPayload] = await Promise.all([
          profileResponse.ok ? profileResponse.json() : Promise.resolve(null),
          scrapedResponse.ok ? scrapedResponse.json() : Promise.resolve(null),
          ctaResponse.ok ? ctaResponse.json() : Promise.resolve([]),
        ]);
        if (!profileResponse.ok || !profilePayload) {
          if (profileResponse.status === 404) {
            if (!cancelled) {
              setProfile(null);
              setScrapedPages(normalizeScrapedPages(scrapedPayload));
              setCtas(Array.isArray(ctaPayload) ? ctaPayload as CtaEntry[] : []);
              setProfileMissing(true);
            }
            return;
          }
          throw new Error('profile_not_found');
        }
        if (cancelled) {
          return;
        }
        setProfile(profilePayload as WorkspaceProfile);
        setScrapedPages(normalizeScrapedPages(scrapedPayload));
        setCtas(Array.isArray(ctaPayload) ? ctaPayload as CtaEntry[] : []);
      } catch {
        if (!cancelled) {
          setError('Profil konnte nicht geladen werden.');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadData();

    return () => {
      cancelled = true;
    };
  }, [customerSlug, isAuthenticated]);

  const profileSections = useMemo(
    () => normalizeSections(profile?.profile_sections),
    [profile?.profile_sections],
  );

  const saveProfileSections = async (sections: BackendProfileSections): Promise<void> => {
    const response = await fetch(`${API_BASE}/api/customer/workspace/${encodeURIComponent(customerSlug)}`, {
      method: 'PATCH',
      headers: adminAuthHeaders(),
      body: JSON.stringify({ profile_sections: sections }),
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(payload?.error || 'profile_save_failed');
    }
    setProfile(payload as WorkspaceProfile);
  };

  const saveCtas = async (nextCtas: CtaEntry[]): Promise<void> => {
    const response = await fetch(`${API_BASE}/api/customer/workspace/${encodeURIComponent(customerSlug)}/ctas`, {
      method: 'PUT',
      headers: adminAuthHeaders(),
      body: JSON.stringify(nextCtas),
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(payload?.error || 'cta_save_failed');
    }
    setCtas(nextCtas);
  };

  const pageClasses = isDarkMode
    ? 'bg-slate-950 text-slate-50'
    : 'bg-slate-100 text-slate-950';
  const mutedClasses = isDarkMode ? 'text-slate-300' : 'text-slate-500';

  return (
    <main className={`min-h-screen px-4 py-6 sm:px-6 lg:px-8 ${pageClasses}`}>
      <div className="mx-auto max-w-5xl space-y-5">
        <header className="flex flex-col gap-4 border-b border-slate-200/70 pb-5 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <button
              type="button"
              onClick={() => navigate(`/dashboard/${customerSlug}`)}
              className={`mb-4 inline-flex items-center gap-2 text-sm font-semibold ${mutedClasses}`}
            >
              <ArrowLeft className="h-4 w-4" />
              Dashboard
            </button>
            <h1 className="text-2xl font-semibold sm:text-3xl">Kundenprofil prüfen</h1>
            <p className={`mt-1 text-sm ${mutedClasses}`}>Scope: {customerSlug} / {workspaceSlug} / {agentSlug}</p>
          </div>
          <button
            type="button"
            onClick={() => navigate(`/setup/prompt/${customerSlug}/${workspaceSlug}/${agentSlug}`)}
            className="inline-flex items-center justify-center rounded-lg bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
          >
            Prompt prüfen
          </button>
        </header>

        {isLoading ? (
          <div className={`flex items-center gap-3 rounded-lg border px-4 py-3 text-sm ${isDarkMode ? 'border-white/10 bg-slate-900 text-slate-300' : 'border-slate-200 bg-white text-slate-600'}`}>
            <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
            Profil wird geladen...
          </div>
        ) : null}

        {error ? <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div> : null}

        <div className={`rounded-lg border p-4 text-sm leading-6 ${isDarkMode ? 'border-blue-400/25 bg-blue-950/20 text-blue-100' : 'border-blue-200 bg-blue-50 text-blue-900'}`}>
          Dieses Website-Profil ist die gemeinsame Unternehmens- und Faktenbasis für alle Avatare von {customerSlug}.
          Der nächste Schritt erzeugt daraus einen eigenen Behaviour-Vorschlag nur für {agentSlug}.
        </div>

        {profileMissing && !error ? (
          <div className={`rounded-lg border p-4 text-sm leading-6 ${isDarkMode ? 'border-white/10 bg-slate-900 text-slate-300' : 'border-slate-200 bg-white text-slate-600'}`}>
            Für diese Customer-ID gibt es noch kein Website-Profil aus dem neuen Setup-Scraping.
            Das Verhalten kann trotzdem über den gespeicherten Behavior-Modifier bearbeitet werden.
          </div>
        ) : null}

        {profile ? (
          <CustomerProfilePanel
            workspaceSlug={customerSlug}
            website={profile.website || ''}
            productWebsite={profile.productWebsite || ''}
            lastScrapedAt={profile.lastScrapedAt}
            initialProfileSections={profileSections}
            scrapedPages={scrapedPages}
            isLoading={isLoading}
            scrapedError={null}
            onSaveSections={saveProfileSections}
            ctas={ctas}
            onSaveCtas={saveCtas}
            isDarkMode={isDarkMode}
          />
        ) : null}
      </div>
    </main>
  );
};

export default SetupProfileReviewPage;
